import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { VisemeTimeline } from '../audio/types.js';

/**
 * Phase-2 lip-sync session (Sprint 28.11+). Pairs a decoded TTS buffer with
 * the viseme keyframe series that drives the mouth animation. Kept atomic so
 * the visemeDriver always reads a coherent (buffer, timeline) pair.
 */
export interface SpeechSession {
  buffer: AudioBuffer;
  timeline: VisemeTimeline;
}

interface AudioState {
  sfxVolume: number; // 0..1
  muted: boolean;
  /** Ambient music bed volume (C-audio). 0..1, persisted. */
  musicVolume: number;
  /** Whether the ambient music bed plays. Opt-in (false) — browser autoplay
   * policy gates the AudioContext on a user gesture, and music is a stronger
   * preference than SFX, so we don't start it uninvited. Persisted. */
  musicEnabled: boolean;
  setVolume: (v: number) => void;
  toggleMuted: () => void;
  setMusicVolume: (v: number) => void;
  toggleMusicEnabled: () => void;
  /**
   * Active speech session (Sprint 28.11 T2). Replaces 28.10's
   * `currentSpeechAudio: AudioBuffer | null` stub. Transient — NOT persisted
   * (AudioBuffer is non-serializable and per-session decoded audio shouldn't
   * survive reloads). T6 PersonaReaction reads this to spin up its lip-sync
   * rAF loop when a message lands with TTS.
   */
  currentSpeech: SpeechSession | null;
  setCurrentSpeech: (s: SpeechSession | null) => void;
}

export const useAudioStore = create<AudioState>()(
  persist(
    (set) => ({
      sfxVolume: 0.5,
      muted: false,
      musicVolume: 0.4,
      musicEnabled: false,
      currentSpeech: null,
      setVolume: (v) => set({ sfxVolume: Math.max(0, Math.min(1, v)) }),
      toggleMuted: () => set((s) => ({ muted: !s.muted })),
      setMusicVolume: (v) => set({ musicVolume: Math.max(0, Math.min(1, v)) }),
      toggleMusicEnabled: () => set((s) => ({ musicEnabled: !s.musicEnabled })),
      setCurrentSpeech: (s) => set({ currentSpeech: s }),
    }),
    {
      name: 'jubuddy:audio-store:v1',
      // Explicit allowlist — `currentSpeech` is transient and excluded
      // (AudioBuffer is not JSON-serializable; per-session lip-sync state
      // should never survive a reload).
      partialize: (s) => ({
        sfxVolume: s.sfxVolume,
        muted: s.muted,
        musicVolume: s.musicVolume,
        musicEnabled: s.musicEnabled,
      }),
    },
  ),
);
